import React, { useEffect, useRef, useMemo, useState } from 'react'
import { Resizable } from 're-resizable'
import { Transforms, Editor } from 'slate'
import { useFocused, useSelected, ReactEditor } from 'slate-react'
import { cx } from '@emotion/css'

//@ts-ignore
import styles from './style.module.css'
import { getAssetType } from '../../image/Modal/util'
import { Tooltip, Icon } from '../../../../../../../../index'
import { EmbedBtnGrp } from '../inlineReference'
import { handleDelete } from '../../../../utils/actions'
import { pushRouteFromEditor, type EditorRouteData } from '../utils/pushRouteFromEditor'
import { cbModal } from '../../../../../../../..'
import { EditCaptionAlt } from './EditCaptionAlt'
import { placeholderImageSrc } from '../../../image/placeholderImageSrc'
import { InfoIcon } from './components'
import TooltipWithErrorBoundary from 'utils/components/TooltipErrorBoundary'

const tooltipOffset: [number, number] = [0, -15];

const Asset = (props) => {
  const containDivRef = useRef(null);
  const [boundaryEl, setBoundaryEl] = useState('viewport');

  const { children, attributes, editor, entry, element, attrs, slatePath } = props
  const icon = getAssetType(element?.attrs?.['asset-type'])
  const isFocused = useFocused()
  const isSelected = useSelected()
  const isHighlight = isFocused && isSelected
  const isError = entry['error'],
    canUpdate = entry?.['_embed_permission']?.['update'],
    canRead = entry?.['_embed_permission']?.['read']
  const isDisabled = entry['disable']

  const { history, apiKey } = editor?.requestProps || {}
  const assetUid = element?.attrs?.['asset-uid'],
    displayType = element?.attrs?.['display-type']
  const imgRef = useRef(null)
  const parentRef = useRef(null)
  const isInline = element?.attrs?.inline
  const isInlineImage = isInline && !icon
  const isDefault = element?.attrs?.['default']
  const alignment = element?.attrs?.style?.['text-align']
  const currentBranch = editor?.requestProps?.branch
  const imageSizeInputEnabled = editor?.imageSizeInputEnabled;
  const handleEdit = () => {
    if (editor.requestProps?.['userAnalytics']) {
      const userAnalytics = editor.requestProps['userAnalytics']
      const metaData = {
        stack_api_key: editor.requestProps['apiKey'],
        json_rte_field_uid: editor.requestProps['field_uid'],
        json_rte_field_type: editor.requestProps['field_type'],
        embedded_asset_type: element['attrs']['display-type'],
        embedded_asset_uid: element['attrs']['asset-uid'],
        mixpanel: true
      }
      userAnalytics.trackEvent(`View Embedded Asset Details`, metaData)
    }

    if (editor.referenceActions?.editAssetReference) {
      editor.referenceActions?.editAssetReference({ apiKey, assetUid })
      return;
    }

    const pathname = `/stack/${apiKey}/assets/${assetUid}`;
    const search = currentBranch ? `branch=${currentBranch}` : '';

    const routeData: EditorRouteData = { pathname, search };
    if (editor.isDraft) {
      const state = editor.getReferredObj({ ctUid: element?.attrs?.['content-type-uid'], blockUid: element.uid, pathname, search });
      if (state.referredObjPayload) state.referredObjPayload.is_asset = true;
      routeData.state = state;
    }
    pushRouteFromEditor(editor, routeData);
  }

  const handleCaption = () => {
    editor.setFullScreen()
    let modalProps: any = {
      customClass: 'asset-edit-modal',
    }
    if (imageSizeInputEnabled) {
      modalProps = {
        size: 'customSize',
        customClass: 'asset-edit-modal-big',
      }
    }
    modalProps.onClose = () => editor.requestProps?.onAutoDraftModalClose?.()
    return cbModal({
      component: (props) => <EditCaptionAlt imageSizeInputEnabled={imageSizeInputEnabled} entry={entry} editor={editor} element={element} slatePath={slatePath} {...props}></EditCaptionAlt>,
      modalProps
    })
  }

  useEffect(() => {
    if (containDivRef.current) {
      const closestBoundary = containDivRef.current.closest('#scrte-editable');
      setBoundaryEl(closestBoundary || 'viewport');
    }
  }, []);

  useEffect(() => {
    try {
      if (!isInline && !alignment) {
        return
      }
      const [assetNode] = Editor.node(editor, ReactEditor.findPath(editor, element))
      const assetWrapper = assetNode && ReactEditor.toDOMNode(editor, assetNode)?.parentElement
      if (assetWrapper && assetWrapper.nodeName === 'P') {
        assetWrapper.setAttribute('style', 'overflow: hidden')
      }
    }
    catch (err) {
      console.log('err', err)
    }
  }, [element])

  const popperOptions = {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: boundaryEl
        }
      }
    ],
  }

  const applyAssetStyles = (attrs, width, height) => {
    const newAttrs = { ...attrs };
    newAttrs['width'] = width;
    const newStyles = { ...newAttrs.style }
    const newRedactorAttrs = { ...newAttrs['redactor-attributes'] }
    newAttrs.style = { ...newStyles }
    newAttrs['redactor-attributes'] = { ...newRedactorAttrs }
    newAttrs['redactor-attributes']['height'] = `${height}`
    newAttrs.style['max-width'] = `${width}px`
    newAttrs.style['width'] = `${width}px`
    newAttrs.style['max-height'] = `${height}px`
    newAttrs.style['height'] = `${height}px`
    newAttrs['max-width'] = `${width}`
    newAttrs['width'] = `${width}`
    newAttrs['max-height'] = `${height}`
    newAttrs['height'] = `${height}`
    return newAttrs
  }

  const onResizeStop = (event, dir, el, size) => {
    let path = ReactEditor.findPath(editor, element)
    let { attrs } = element
    let width = imgRef.current.clientWidth
    let height = imgRef.current.clientHeight
    let newAttrs = applyAssetStyles(attrs, width, height)
    if (newAttrs) {
      Transforms.setNodes(
        editor,
        //@ts-ignore
        { attrs: { ...newAttrs } },
        {
          at: path
        }
      )
    }
  }

  const handleImageError = (e) => {
    e.target.src = placeholderImageSrc
  }

  const initialDimensions = {
    width: `${element.attrs.width ? `${element.attrs.width}px` : displayType === 'download' ? '180px' : 'auto'}`,
    height: `${element.attrs.height ? `${element.attrs.height}px` : displayType === 'download' ? '180px' : 'auto'}`
  }

  let alignmentStyle
  if (alignment) {
    if (alignment === 'center' || alignment === 'justify' || !isInline) {
      delete attrs.style['float']
    }
    const marginAlignment = {
      center: { margin: 'auto' },
      left: { marginRight: 'auto' },
      right: { marginLeft: 'auto' }
    }
    alignmentStyle = marginAlignment[alignment]
  }

  if (isInline) {
    alignmentStyle = { display: 'inline-block', margin: "0 0.15rem -1px" }
    if (alignment) {
      alignmentStyle.float = alignment
    }
  }

  alignmentStyle = isInline ? { ...alignmentStyle } : alignmentStyle

  const captionWidth = element?.attrs?.width ? `${element?.attrs?.width}px` : 'auto'
  const path = ReactEditor.findPath(editor, element)
  const handleClick = () => {
    Transforms.select(editor, path)
  }
  return (
    <div ref={containDivRef} onClick={handleClick} style={{ ...alignmentStyle }} className={styles['scrte-asset-wrapper']} data-testid={"embed-asset-wrapper"}>
      {children}
      <div contentEditable={false}>
        <TooltipWithErrorBoundary
          zIndex={909}
          className="p-0"
          variantType="light"
          appendTo={document.body}
          offset={tooltipOffset}
          position="bottom"
          popperOptions={popperOptions}
          content={
            <div className={styles['embed--btn-group']}>
              <EmbedBtnGrp
                error={entry['error']}
                isDefault={isDefault}
                isDisabled={isDisabled}
                isError={isError}
                handleCaption={(canUpdate || isDefault) && handleCaption}
                handleEdit={canRead && handleEdit}
                handleDelete={(e) => {
                  handleDelete(editor, element)
                  editor.requestProps?.onAutoDraftElementDelete?.()
                }}
                handleUnlink={undefined}
                editor={editor}
                type="asset"
                attributes={{ ...props.attributes, ...props.attrs, element }}
                handleOpenInModal={undefined}
              />
            </div>
          }>
          <span {...attrs} data-type="asset">
            <div ref={parentRef}>
              <Resizable
                lockAspectRatio={true}
                onResizeStop={onResizeStop}
                size={initialDimensions}
                className={styles['scrte--img-container']}
                handleClasses={{ right: "scrte-img-resizer--right" }}
                handleStyles={{
                  right: { right: 0, width: '15px' },
                  left: { left: 0, width: '15px' },
                  bottom: { bottom: '0px' }
                }}>
                <div ref={imgRef} style={{ width: '100%', height: '100%' }} data-testid={'embed-asset-img-element'}>
                  <div
                    className={cx(
                      !isInlineImage && styles['embed-asset'],
                      !isInlineImage && displayType === 'download' && styles['embed-download'],
                      isInlineImage && styles['embed-asset--inline'],
                      isInlineImage && imageSizeInputEnabled && styles['embed-asset--inline-full'],
                      isHighlight && styles['embed-asset-active']
                    )}>
                    {!icon && (
                      <img
                        onError={handleImageError}
                        src={entry.url || placeholderImageSrc}
                        style={{ width: '100%', height: imageSizeInputEnabled ? '100%' : 'auto' }}
                        data-testid={"embed-image"}
                      />
                    )}
                    {!isInlineImage && (
                      <div className={styles['embed-icon']}>
                        <Icon icon="Embed" />
                      </div>
                    )}
                    {isError || isDisabled ? (
                      <InfoIcon className={styles['center']} isDisabled={isDisabled} error={entry['error']} />
                    ) : (
                      <Icon className={styles['center']} icon={icon} size="large" />
                    )}
                  </div>
                </div>
                <span className={styles['scrte-image-resizer-feedback-left']}>
                  <span className={styles['scrte-image-resizer-feedback']}></span>
                </span>
                <span className={styles['scrte-image-resizer-feedback-right']} data-testid={"asset-resizable--right"}>
                  <span className={styles['scrte-image-resizer-feedback']}></span>
                </span>
              </Resizable>
            </div>
          </span>
        </TooltipWithErrorBoundary>
        {element?.attrs?.['asset-caption'] && (
          <p className={styles['scrte--caption']} style={{ width: captionWidth }} data-testid={'embed-asset-caption'}>{element.attrs['asset-caption']}</p>
        )}
      </div>
    </div>
  )
}

export default Asset
